import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Clock, CheckCircle2, AlertTriangle, Ban, Calendar } from "lucide-react";

const STATUS_CLS = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
  cancelled: "bg-gray-100 text-gray-700 border-gray-300",
};

const STATUS_ICON = {
  pending: Clock,
  sent: CheckCircle2,
  failed: AlertTriangle,
  cancelled: Ban,
};

const TRIGGER_TYPE_LABEL = {
  event: "Evento síncrono",
  transition: "Transición FSM",
  cron: "Cron programado",
};

export default function SequenceRunsPage() {
  const { id: sequenceId } = useParams();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sinceDays, setSinceDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status_", statusFilter);
      params.set("since_days", String(sinceDays));
      params.set("limit", "200");
      const { data: resp } = await api.get(`/admin/emails/sequences/${sequenceId}/runs?${params.toString()}`);
      setData(resp);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando runs");
    } finally {
      setLoading(false);
    }
  }, [sequenceId, statusFilter, sinceDays]);

  useEffect(() => { load(); }, [load]);

  const fmtDt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
  };

  const seq = data?.sequence;

  return (
    <div className="container-prose py-8" data-testid="sequence-runs-page">
      <div className="mb-2">
        <Link to="/admin/emails" className="text-xs text-gray-500 hover:text-brand-burgundy inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Volver a Emails
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-brand-burgundy font-medium mb-1">Secuencia · Aplicaciones</p>
          <h1 className="font-serif text-2xl text-gray-900" data-testid="seq-name">
            {seq?.name || "Cargando..."}
          </h1>
          {seq && (
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
              <code className="bg-gray-100 px-1.5 py-0.5 rounded-sm text-gray-700">{seq.trigger_event}</code>
              <span className="px-2 py-0.5 border border-gray-300 rounded-sm text-gray-700">
                {TRIGGER_TYPE_LABEL[seq.trigger_type] || seq.trigger_type || "event"}
              </span>
              {seq.target_state && (
                <span className="px-2 py-0.5 border border-brand-burgundy/30 bg-brand-cream-soft text-brand-burgundy rounded-sm">
                  → {seq.target_state}
                </span>
              )}
              {!!seq.delay_minutes && (
                <span className="text-gray-500">delay {seq.delay_minutes >= 1440 ? `${Math.round(seq.delay_minutes/1440)}d` : `${seq.delay_minutes}m`}</span>
              )}
              {seq.cron_days?.length > 0 && (
                <span className="text-gray-500">días: [{seq.cron_days.join(", ")}]</span>
              )}
              <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${seq.active ? "bg-emerald-50 text-emerald-800" : "bg-gray-100 text-gray-700"}`}>
                {seq.active ? "Activa" : "Inactiva"}
              </span>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-sm" data-testid="runs-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refrescar
        </Button>
      </div>

      {/* Counts */}
      {data?.counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="runs-counts">
          {Object.entries(data.counts).map(([k, v]) => {
            const Icon = STATUS_ICON[k];
            return (
              <button
                key={k}
                onClick={() => setStatusFilter(statusFilter === k ? "" : k)}
                className={`text-left p-3 border rounded-sm transition-colors ${statusFilter === k ? STATUS_CLS[k] + " border-2" : "bg-white border-gray-200 hover:border-gray-400"}`}
                data-testid={`runs-filter-${k}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gray-500">
                  <Icon className="w-3.5 h-3.5" /> {k}
                </div>
                <div className="text-2xl font-serif text-gray-900 mt-1 tabular-nums">{v}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters bar */}
      <div className="flex items-center gap-3 mb-4 text-xs" data-testid="runs-filter-bar">
        <div className="flex items-center gap-1 text-gray-500">
          <Calendar className="w-3.5 h-3.5" />
          <span>Últimos</span>
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            className="bg-white border border-gray-300 rounded-sm px-2 py-1 text-xs"
            data-testid="runs-since-days"
          >
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
            <option value={180}>180 días</option>
          </select>
        </div>
        {statusFilter && (
          <button onClick={() => setStatusFilter("")} className="text-rose-700 hover:underline" data-testid="runs-clear-status">
            ✕ Limpiar filtro {statusFilter}
          </button>
        )}
      </div>

      {/* Runs table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="runs-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-[10px] uppercase tracking-widest text-gray-500">
              <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
              <th className="text-left px-4 py-2.5 font-medium">Destinatario</th>
              <th className="text-left px-4 py-2.5 font-medium">Estado</th>
              <th className="text-left px-4 py-2.5 font-medium">Origen</th>
              <th className="text-left px-4 py-2.5 font-medium">Contexto</th>
            </tr>
          </thead>
          <tbody>
            {!data?.items?.length && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-12 italic">Sin aplicaciones registradas en este rango.</td></tr>
            )}
            {data?.items?.map((it) => {
              const Icon = STATUS_ICON[it.status] || Clock;
              return (
                <tr key={`${it.source}-${it.id}`} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`runs-row-${it.id}`}>
                  <td className="px-4 py-2.5 text-xs">
                    <div className="text-gray-900">{fmtDt(it.created_at)}</div>
                    {it.scheduled_at && it.source === "queue" && (
                      <div className="text-[10px] text-gray-400">prog: {fmtDt(it.scheduled_at)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{it.to_email || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border rounded-sm ${STATUS_CLS[it.status] || "bg-gray-100 text-gray-700"}`}>
                      <Icon className="w-3 h-3" /> {it.status || "—"}
                    </span>
                    {it.attempts ? <span className="text-[10px] text-gray-400 ml-1">({it.attempts} intentos)</span> : null}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    <span className={`px-1.5 py-0.5 rounded-sm border ${it.source === "queue" ? "border-brand-burgundy/30 text-brand-burgundy bg-brand-cream-soft" : "border-gray-300 text-gray-600 bg-gray-50"}`}>
                      {it.source === "queue" ? "Scheduler" : "Log síncrono"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-gray-600 font-mono max-w-xs truncate" title={JSON.stringify(it.metadata || {})}>
                    {it.metadata?.case_id ? `case: ${String(it.metadata.case_id).slice(0,8)}…` : ""}
                    {it.metadata?.day ? `day ${it.metadata.day}` : ""}
                    {it.last_error ? <span className="text-rose-600"> · err: {it.last_error}</span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data?.items?.length === 200 && (
        <p className="text-[11px] text-amber-700 mt-2">Mostrando 200 items máximo. Ajusta el rango de días para más resultados.</p>
      )}
    </div>
  );
}
